import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Save, Sparkles, Loader2, RefreshCw, Mic, MicOff, StickyNote, ArrowRightToLine } from "lucide-react";
import { Panel, Btn, Lbl, RT } from "./ui";

const ACTION_LABELS = {
  subject_contacted: "Subject Contacted", complainant_contacted: "Complainant Contacted",
  witness_interviewed: "Witness Interviewed", supervisor_notified: "Supervisor Notified",
  ems_requested: "EMS Requested", ems_responded: "EMS Responded", fire_requested: "Fire Requested",
  fire_responded: "Fire Responded", person_identified: "Person Identified",
  records_check_conducted: "Records Check Conducted", scene_secured: "Scene Secured",
  facility_management_notified: "Facility Management Notified", evidence_collected: "Evidence Collected",
  photographs_taken: "Photographs Taken", bwc_available: "Body-Worn Camera Available",
  arrest_made: "Arrest Made", citation_issued: "Citation Issued", warning_issued: "Warning Issued",
  referral_made: "Referral Made", report_completed: "Report Completed",
};

export function OfficerNotes({ rep, canEdit, onChanged }) {
  const [text, setText] = useState("");
  const [dictating, setDictating] = useState(false);
  const [rec, setRec] = useState(null);
  const notes = rep.officer_notes || [];
  const startDictation = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice dictation is not supported in this browser"); return; }
    const r = new SR();
    r.continuous = true; r.interimResults = true; r.lang = "en-US";
    r.onresult = (e) => {
      let t = "";
      for (const res of e.results) t += res[0].transcript;
      setText(t);
    };
    r.onerror = () => { setDictating(false); toast.error("Dictation error — check microphone permission"); };
    r.onend = () => setDictating(false);
    r.start(); setRec(r); setDictating(true);
    toast.info("Listening — review the transcription before saving");
  };
  const stopDictation = () => { rec?.stop(); setDictating(false); };
  const save = async () => {
    if (!text.trim()) { toast.error("Note text required"); return; }
    try {
      await client.post(`/rms-reports/${rep.id}/notes`, { text: text.trim(), unit: rep.reporting_officer?.unit || "", source: dictating ? "voice" : "typed" });
      setText(""); toast.success("Officer note recorded"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Note failed"); }
  };
  const toNarrative = async (n) => {
    try {
      const r = await client.post(`/rms-reports/${rep.id}/notes/${n.nid}/to-narrative`, { section_index: 2 });
      toast.success(`Note inserted into ${r.data.section} — review the narrative`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Insert failed"); }
  };
  return (
    <Panel title={`Officer Notes (${notes.length})`} testid="panel-officer-notes">
      <div className="font-mono text-[9px] mb-2" style={{ color: RT.dim }}>
        Operational notes stay separate from the formal narrative. Insert a note only after review — insertion is audited.
      </div>
      {notes.map((n) => (
        <div key={n.nid} className="border rounded-sm p-2 mb-1.5" style={{ borderColor: RT.border }} data-testid={`note-${n.nid}`}>
          <div className="font-mono text-[10px] font-bold" style={{ color: RT.head }}>
            {n.time} HRS — {n.unit ? `UNIT ${n.unit}` : n.officer} <span className="font-normal" style={{ color: RT.dim }}>· {n.source} · {n.officer}</span>
          </div>
          <div className="text-[12px] mt-0.5" style={{ color: RT.text }}>“{n.text}”</div>
          {canEdit && !n.in_narrative && (
            <button data-testid={`note-insert-${n.nid}`} onClick={() => toNarrative(n)}
              className="mt-1 flex items-center gap-1 text-[9px] font-head font-bold uppercase" style={{ color: RT.head }}>
              <ArrowRightToLine size={10} /> Add to Narrative
            </button>
          )}
          {n.in_narrative && <div className="mt-1 font-mono text-[9px] text-[#15803d]">IN NARRATIVE</div>}
        </div>
      ))}
      <div className="mt-2">
        <textarea data-testid="note-text" value={text} onChange={(e) => setText(e.target.value)} rows={2}
          placeholder={dictating ? "Listening… speak your note" : "+ Add note (typed or dictated)"}
          className="w-full border px-2 py-1.5 text-[12px] rounded-sm outline-none"
          style={{ background: RT.input, borderColor: dictating ? "#b91c1c" : RT.border, color: RT.text }} />
        <div className="flex gap-2 mt-1">
          <Btn small onClick={save} testid="note-save"><StickyNote size={11} /> Save Note</Btn>
          {!dictating
            ? <Btn small color="#7c3aed" onClick={startDictation} testid="note-dictate"><Mic size={11} /> Dictate Note</Btn>
            : <Btn small color="#b91c1c" onClick={stopDictation} testid="note-dictate-stop"><MicOff size={11} /> Stop &amp; Review</Btn>}
        </div>
      </div>
    </Panel>
  );
}

export default function SectionNarrative({ rep, meta, canEdit, onChanged }) {
  const [sections, setSections] = useState(rep.narrative_sections);
  const [actions, setActions] = useState(rep.actions);
  const [aiBusy, setAiBusy] = useState(null);
  const save = async () => {
    try {
      await client.patch(`/rms-reports/${rep.id}/section`, { section: "narrative_sections", data: sections });
      await client.patch(`/rms-reports/${rep.id}/section`, { section: "actions", data: actions });
      toast.success("Narrative & actions saved");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const aiAssist = async (i) => {
    setAiBusy(i);
    try {
      const r = await client.post(`/rms-reports/${rep.id}/narrative-ai`, { text: sections[i].text });
      setSections(sections.map((s, j) => j === i ? { ...s, text: r.data.text } : s));
      toast.success("AI suggestion applied — review before saving. AI never invents facts.");
    } catch (e) { toast.error(e.response?.data?.detail || "AI assist failed"); }
    setAiBusy(null);
  };
  return (
    <div className="space-y-3">
      {rep.cfs_summary && (
        <Panel title="Initial Complaint / CFS Summary" testid="panel-cfs-summary">
          <div className="font-mono text-[11px] font-bold" style={{ color: RT.text }}>{rep.cfs_summary}</div>
        </Panel>
      )}
      <div className="grid lg:grid-cols-[1fr_320px] gap-3 items-start">
        <div className="space-y-3">
          <Panel title="Officer Narrative" testid="panel-narrative"
            right={canEdit && (
              <div className="flex items-center gap-2">
                <select data-testid="narrative-style" value={rep.report_style || "PUBLIC SAFETY / LAW ENFORCEMENT"}
                  onChange={async (e) => {
                    const style = e.target.value;
                    if (!rep.narrative_auto_generated && !window.confirm(`Switching to "${style}" regenerates the draft from CAD data and replaces officer-typed text. Continue?`)) return;
                    try { await client.post(`/rms-reports/${rep.id}/regenerate`, { style }); toast.success(`Draft regenerated — ${style}`); onChanged(); }
                    catch (err) { toast.error(err.response?.data?.detail || "Style change failed"); }
                  }}
                  className="border px-1.5 py-1 text-[9px] font-mono uppercase rounded-sm outline-none bg-white"
                  style={{ borderColor: "#7da7c7", color: RT.text }}>
                  {(meta?.report_styles || ["PUBLIC SAFETY / LAW ENFORCEMENT", "CAD SUMMARY", "SECURITY INCIDENT", "EMS ASSIST", "ADMINISTRATIVE"]).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <Btn small color="#64748b" onClick={async () => {
                  if (!rep.narrative_auto_generated && !window.confirm("Regenerating replaces the ENTIRE draft with a fresh auto-generated narrative from current CAD data. Officer-typed text will be lost. Continue?")) return;
                  try { await client.post(`/rms-reports/${rep.id}/regenerate`, { style: rep.report_style }); toast.success("Draft regenerated from CAD"); onChanged(); }
                  catch (e) { toast.error(e.response?.data?.detail || "Regenerate failed"); }
                }} testid="narrative-regenerate"><RefreshCw size={11} /> Regenerate Draft</Btn>
                <Btn small onClick={save} testid="narrative-save"><Save size={11} /> Save Narrative</Btn>
              </div>
            )}>
            {rep.narrative_auto_generated && (
              <div className="mb-2 border-l-4 border-[#b45309] bg-[#fdf6ec] px-2 py-1.5 font-mono text-[10px] font-bold text-[#b45309]" data-testid="auto-draft-banner">
                AUTO-GENERATED DRAFT — built only from recorded CAD/MDT data. Review, edit and save before submission; auto-generated language is never an officer-verified fact.
              </div>
            )}
        <div className="space-y-3">
          {sections.map((s, i) => (
            <div key={s.heading}>
              <div className="flex items-center gap-2">
                <Lbl>{s.heading}</Lbl>
                {canEdit && (s.text || "").length >= 30 && (
                  <button data-testid={`narrative-ai-${i}`} onClick={() => aiAssist(i)} disabled={aiBusy !== null}
                    className="flex items-center gap-1 text-[9px] font-head font-bold uppercase text-[#7c3aed] mb-0.5 disabled:opacity-50">
                    {aiBusy === i ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} AI Polish (review required)
                  </button>
                )}
              </div>
              <textarea data-testid={`narrative-sec-${i}`} value={s.text || ""} disabled={!canEdit}
                rows={s.heading === "INVESTIGATION" || s.heading === "ARRIVAL / OBSERVATIONS" ? 4 : 2}
                onChange={(e) => setSections(sections.map((x, j) => j === i ? { ...x, text: e.target.value } : x))}
                className="w-full border px-2 py-1.5 text-[12.5px] rounded-sm outline-none focus:border-[#1d5d90] disabled:opacity-60"
                style={{ background: RT.input, borderColor: RT.border, color: RT.text }} />
            </div>
          ))}
        </div>
        <div className="mt-1 font-mono text-[9px]" style={{ color: RT.dim }}>
          AI assistance suggests wording only — it never invents facts. You must review and affirmatively save all AI-assisted content.
        </div>
      </Panel>
      {(rep.call_type_questions || []).length > 0 && (
        <Panel title="Call-Type Checklist (prompts)" testid="panel-checklist">
          <ul className="space-y-1">
            {rep.call_type_questions.map((q, i) => (
              <li key={i} className="text-[11px] font-mono flex gap-2" style={{ color: RT.dim }}>
                <span style={{ color: RT.head }}>▸</span> {q}
              </li>
            ))}
          </ul>
        </Panel>
      )}
      <Panel title="Actions Taken" testid="panel-actions">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
          {Object.entries(ACTION_LABELS).map(([k, label]) => (
            <label key={k} className="flex items-center gap-1.5 text-[11px] font-mono cursor-pointer" style={{ color: RT.text }}>
              <input data-testid={`action-${k}`} type="checkbox" checked={!!actions[k]} disabled={!canEdit}
                onChange={(e) => setActions({ ...actions, [k]: e.target.checked })} className="accent-[#1d5d90]" />
              {label}
            </label>
          ))}
        </div>
        <div className="mt-2">
          <Lbl>Other Action Taken</Lbl>
          <input data-testid="action-other" value={actions.other_action || ""} disabled={!canEdit}
            onChange={(e) => setActions({ ...actions, other_action: e.target.value })}
            className="w-full border px-2 py-1 text-[12px] font-mono rounded-sm outline-none disabled:opacity-60"
            style={{ background: RT.input, borderColor: RT.border, color: RT.text }} />
        </div>
      </Panel>
        </div>
        <OfficerNotes rep={rep} canEdit={canEdit} onChanged={onChanged} />
      </div>
    </div>
  );
}
